import { Injectable, inject } from '@angular/core';
import { environment } from '../../../../environments/environment';
import {
  AbsenceReasonsSlug,
  ClientsSlug,
  ColorsSlug,
  FilesSlug,
  GeneratedPdfSlug,
  HolidayTemplatesSlug,
  InvitationSlug,
  InvoiceDocumentOverviewSlug,
  InvoiceDocumentsSlug,
  ProjectExpensesSlug,
  ProjectsSlug,
  StatusSlug,
  TagSlug,
  TasksSlug,
  TimeBlockBulkDelete,
  TimeBlocksSlug,
  TimerSlug,
  TodoPlannerSlug,
  UsersSlug,
} from '../../constants-repository/api-parts-and-querystrings.constants';
import { UrlSegmentDelimiter } from '../../constants-repository/app-style.constants';
import { HttpOperation } from '../../enums/http-operation';
import { AuthEndpointsService } from './auth-endpoints.service';

@Injectable({
  providedIn: 'root',
})
export class EndpointService {
  // DI
  public authEndpointsService = inject(AuthEndpointsService);

  private readonly PREFIX = environment.apiPath + UrlSegmentDelimiter;

  // ////////////############ Time Blocks ############////////////
  public getTimeBlockSlug(operation: HttpOperation, id?: number): string {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return this.PREFIX + TimeBlocksSlug + UrlSegmentDelimiter;
    }
    return this.buildUrlWithId(TimeBlocksSlug, id);
  }

  public getTimeBlockSlugForBulkDelete(): string {
    return (
      this.PREFIX + TimeBlocksSlug + UrlSegmentDelimiter + TimeBlockBulkDelete + UrlSegmentDelimiter
    );
  }

  // ////////////############ Clients ############////////////
  public getClientsSlug(operation: HttpOperation, id?: number): string {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return this.PREFIX + ClientsSlug + UrlSegmentDelimiter;
    }
    return this.buildUrlWithId(ClientsSlug, id);
  }

  // ////////////############ Projects ############////////////
  public getProjectsSlug(operation: HttpOperation, id?: number): string {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return this.PREFIX + ProjectsSlug + UrlSegmentDelimiter;
    }
    return this.buildUrlWithId(ProjectsSlug, id);
  }

  // ////////////############ Project or task Status ############////////////
  public getProjectOrTaskStatusSlug(operation: HttpOperation, id?: number): string {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return this.PREFIX + StatusSlug + UrlSegmentDelimiter;
    }
    return this.buildUrlWithId(StatusSlug, id);
  }

  // ////////////############ Project Expenses ############////////////
  getProjectExpensesSlug(operation: HttpOperation, id?: number) {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return this.PREFIX + ProjectExpensesSlug + UrlSegmentDelimiter;
    }
    return this.buildUrlWithId(ProjectExpensesSlug, id);
  }

  // ////////////############ Tasks ############////////////
  public getTasksSlug(operation: HttpOperation, id?: number): string {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return this.PREFIX + TasksSlug + UrlSegmentDelimiter;
    }
    return this.buildUrlWithId(TasksSlug, id);
  }

  // ////////////############ Users ############////////////
  public getUsersSlug(operation: HttpOperation, id?: number): string {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return this.PREFIX + UsersSlug + UrlSegmentDelimiter;
    }
    return this.buildUrlWithId(UsersSlug, id);
  }

  // ////////////############ E-Mail Invitation ############////////////
  public getInvitationSlug(operation: HttpOperation, actionSegment: string, id?: number): string {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return (
        this.PREFIX + InvitationSlug + UrlSegmentDelimiter + actionSegment + UrlSegmentDelimiter
      );
    }
    return this.buildUrlWithId(InvitationSlug, id) + actionSegment + UrlSegmentDelimiter;
  }

  // ////////////############ Colors ############////////////
  public getColorsSlug(operation: HttpOperation, id?: number): string {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return this.PREFIX + ColorsSlug + UrlSegmentDelimiter;
    }
    return this.buildUrlWithId(ColorsSlug, id);
  }

  // ////////////############ Timer ############////////////
  public getTimerSlug(operation: HttpOperation, id?: number): string {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return this.PREFIX + TimerSlug + UrlSegmentDelimiter;
    }
    return this.buildUrlWithId(TimerSlug, id);
  }

  // ////////////############ Absence Reasons ############////////////
  public getAbsenceReasonsSlug(operation: HttpOperation, id?: number): string {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return this.PREFIX + AbsenceReasonsSlug + UrlSegmentDelimiter;
    }
    return this.buildUrlWithId(AbsenceReasonsSlug, id);
  }

  // ////////////############ Holiday Templates ############////////////
  public getHolidayTemplatesSlug(operation: HttpOperation, id?: number): string {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return this.PREFIX + HolidayTemplatesSlug + UrlSegmentDelimiter;
    }
    return this.buildUrlWithId(HolidayTemplatesSlug, id);
  }

  // ////////////############ Generated PDF ############////////////
  public getGeneratedPdfSlug(operation: HttpOperation, id?: number): string {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return this.PREFIX + GeneratedPdfSlug + UrlSegmentDelimiter;
    }
    return this.buildUrlWithId(GeneratedPdfSlug, id);
  }

  // ////////////############ Files Handling ############////////////
  public getFilesSlug(operation: HttpOperation, id?: number): string {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return this.PREFIX + FilesSlug + UrlSegmentDelimiter;
    }
    return this.buildUrlWithId(FilesSlug, id);
  }

  // ////////////############ Invoice Documents ############////////////
  public getInvoiceDocumentOverviewSlug(operation: HttpOperation, id?: number): string {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return this.PREFIX + InvoiceDocumentOverviewSlug + UrlSegmentDelimiter;
    }
    return this.buildUrlWithId(InvoiceDocumentOverviewSlug, id);
  }

  public getInvoiceDocumentSlug(operation: HttpOperation, id?: number): string {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return this.PREFIX + InvoiceDocumentsSlug + UrlSegmentDelimiter;
    }
    return this.buildUrlWithId(InvoiceDocumentsSlug, id);
  }

  // ////////////############ Tags ############////////////
  public getTagSlug(operation: HttpOperation, id?: number): string {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return this.PREFIX + TagSlug + UrlSegmentDelimiter;
    }
    return this.buildUrlWithId(TagSlug, id);
  }

  // ////////////############ To-do Planner ############////////////
  public getTodoPlannerSlug(operation: HttpOperation, id?: number): string {
    if (operation === HttpOperation.Post || (operation === HttpOperation.Get && !id)) {
      return this.PREFIX + TodoPlannerSlug + UrlSegmentDelimiter;
    }
    return this.buildUrlWithId(TodoPlannerSlug, id);
  }

  private buildUrlWithId(slug: string, id?: number): string | never {
    if (!id) {
      return this.PREFIX + slug + UrlSegmentDelimiter;
    }
    return this.PREFIX + slug + UrlSegmentDelimiter + id + UrlSegmentDelimiter;
  }
}
